import {
  ALERT_MANAGER_EVENT_TYPE_LOCAL_TOAST,
  ALERT_MANAGER_EVENT_TYPE_TOAST,
} from '../../utils/spds/constants/alert-manager';
import {
  TOAST_DEFAULT_CTA_TIMEOUT,
  TOAST_DEFAULT_TIMEOUT,
  TOAST_ERROR_TYPE,
  TOAST_SUCCESS_TYPE,
  TOAST_SYSTEM_TYPE,
} from '../../utils/spds/constants/feedback';
import Service, { service } from '@ember/service';

/**
 * Generates a user-friendly message for file upload notifications
 * @private
 * @param {number} countUploaded - The number of files uploaded
 * @param {File} file - The File object that was uploaded
 * @returns {string} A formatted message showing file count or filename
 */
function getFileUploadMessage(countUploaded, file) {
  return countUploaded > 1 ? `${countUploaded} files uploaded` : `"${file.name}" uploaded`;
}

/**
 * SPDS Toast Service
 *
 * Provides an API for displaying transient toast notifications.
 * Toasts auto-dismiss after a timeout and appear at the top of the viewport.
 * Supports both global toasts (visible across the app) and local toasts (scoped to containers).
 *
 * @class SpdsToastService
 * @extends Service
 */
export default class SpdsToastService extends Service {
  @service alertManager;

  /**
   * Displays a global toast notification
   * @private
   * @param {object} data - The toast data object
   * @param {string} data.message - The message to display
   * @param {string} data.type - The toast type constant (ERROR, SUCCESS, or SYSTEM)
   * @param {number} data.timeout - Duration in milliseconds before auto-dismiss
   * @param {object} [data.cta] - Optional call-to-action configuration
   * @returns {Promise<void>}
   */
  #displayToast = async data => {
    await this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_TOAST, data);
  };

  /**
   * Displays a local toast notification scoped to a container
   * @private
   * @param {object} data - The toast data object
   * @param {string} data.message - The message to display
   * @param {string} data.type - The toast type constant (ERROR, SUCCESS, or SYSTEM)
   * @param {number} data.timeout - Duration in milliseconds before auto-dismiss
   * @param {object} [data.cta] - Optional call-to-action configuration
   * @returns {Promise<void>}
   */
  #displayLocalToast = async data => {
    await this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_LOCAL_TOAST, data);
  };

  /**
   * Determines the appropriate timeout value for a toast
   * @private
   * @param {object} [cta] - Optional call-to-action configuration
   * @param {number} [timeout] - Custom timeout duration in milliseconds
   * @returns {number} The timeout duration (uses TOAST_DEFAULT_CTA_TIMEOUT if cta exists, TOAST_DEFAULT_TIMEOUT otherwise)
   */
  #setTimeout = (cta, timeout) =>
    timeout ?? (cta ? TOAST_DEFAULT_CTA_TIMEOUT : TOAST_DEFAULT_TIMEOUT);

  /**
   * Displays a global error toast
   * @param {string} message - The error message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.error('Failed to save changes');
   *
   * @example
   * this.toast.error('Error occurred', { timeout: 10000 });
   */
  error = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_ERROR_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a local success toast for file upload completion
   * Shows either the filename or count of multiple files uploaded.
   *
   * @param {number} countUploaded - The number of files uploaded
   * @param {File} file - The File object that was uploaded
   *
   * @example
   * this.toast.fileUploadLocalToast(1, fileObject);
   * // Shows: "filename.pdf" uploaded
   *
   * @example
   * this.toast.fileUploadLocalToast(3, fileObject);
   * // Shows: 3 files uploaded
   */
  fileUploadLocalToast(countUploaded, file) {
    this.localSuccess(getFileUploadMessage(countUploaded, file));
  }

  /**
   * Displays a global success toast for file upload completion
   * Shows either the filename or count of multiple files uploaded.
   *
   * @param {number} countUploaded - The number of files uploaded
   * @param {File} file - The File object that was uploaded
   *
   * @example
   * this.toast.fileUploadToast(1, fileObject);
   * // Shows: "filename.pdf" uploaded
   */
  fileUploadToast(countUploaded, file) {
    this.success(getFileUploadMessage(countUploaded, file));
  }

  /**
   * Displays a local error toast scoped to a container
   * @param {string} message - The error message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.localError('Invalid email format');
   */
  localError = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayLocalToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_ERROR_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a local system toast scoped to a container
   * @param {string} message - The system message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.localSystem('Connection restored');
   */
  localSystem = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayLocalToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_SYSTEM_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a local success toast scoped to a container
   * @param {string} message - The success message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.localSuccess('Settings saved');
   */
  localSuccess = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayLocalToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_SUCCESS_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a global system toast
   * @param {string} message - The system message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.system('Scheduled maintenance in 10 minutes');
   */
  system = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_SYSTEM_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a global success toast
   * @param {string} message - The success message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.success('Changes saved successfully');
   */
  success = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_SUCCESS_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };
}
